import { useEffect, useRef } from 'react';
import { useSiteConfig } from '../context/SiteConfigContext';

const colorMap = {
  bgPrimary: '--bg-primary',
  bgSecondary: '--bg-secondary',
  bgTertiary: '--bg-tertiary',
  textHeading: '--text-heading',
  textAccent: '--text-accent',
  textBody: '--text-body',
  textWhite: '--text-white',
  textMuted: '--text-muted',
  divider: '--divider',
  sidebarBg: '--sidebar-bg',
  sidebarHover: '--sidebar-hover',
  sidebarActive: '--sidebar-active',
};

function hexToRgbTriplet(hex) {
  if (typeof hex !== 'string') return null;
  const match = hex.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) return null;
  let value = match[1];
  if (value.length === 3) {
    value = value.split('').map((c) => c + c).join('');
  }
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

export function useThemeApplier() {
  const { theme } = useSiteConfig();
  const lastValues = useRef({});

  useEffect(() => {
    const root = document.documentElement;
    const next = {};

    Object.entries(theme.colors).forEach(([key, value]) => {
      if (colorMap[key]) next[colorMap[key]] = value;
    });

    const accentTriplet = hexToRgbTriplet(theme.colors.textAccent);
    if (accentTriplet) {
      next['--accent-rgb'] = accentTriplet;
    } else if (lastValues.current['--accent-rgb']) {
      // Not a parseable hex — keep whatever triplet was last applied.
      next['--accent-rgb'] = lastValues.current['--accent-rgb'];
    }

    next['--font-heading'] = theme.fonts.heading;
    next['--font-body'] = theme.fonts.body;
    next['--sidebar-width'] = theme.sizes.sidebarWidth;
    next['--transition-speed'] = theme.sizes.transitionSpeed;

    Object.entries(next).forEach(([prop, value]) => {
      if (lastValues.current[prop] !== value) {
        root.style.setProperty(prop, value);
      }
    });

    lastValues.current = next;
  }, [theme]);
}
